import React from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Checkbox, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { IK1Field } from "store/types";

import { K1EditValue } from "../hooks/use-k1-form";
import { K1MultiField } from "./K1MultiField";

interface Props {
  field: IK1Field;
  value: K1EditValue;
  edited: boolean;
  readOnly: boolean;
  onChange: (key: string, value: K1EditValue) => void;
}

export const K1FieldRow: React.FC<Props> = ({ field, value, edited, readOnly, onChange }) => {
  if (field.is_multiple) {
    return <K1MultiField field={field} value={value} edited={edited} readOnly={readOnly} onChange={onChange} />;
  }

  const isBoolean = field.type === "boolean";
  const isNumber = field.type === "number";

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="8px"
      sx={{
        px: "12px",
        py: "6px",
        minHeight: "36px",
        borderTop: "1px solid #eef2f8",
        "&:first-of-type": { borderTop: "none" },
        bgcolor: edited ? "#effcfd" : "transparent"
      }}
    >
      <Typography sx={{ flex: 1, minWidth: 0, fontSize: "13px", color: "#496682", lineHeight: 1.35 }}>
        {field.name}
        {edited && (
          <MaterialTooltip variant="dark" isShow title="Value differs from the original extracted value">
            <ErrorOutlineIcon sx={{ fontSize: 16, color: "#EBAF01", ml: "6px", verticalAlign: "text-bottom" }} />
          </MaterialTooltip>
        )}
      </Typography>

      {isBoolean ? (
        <Checkbox
          checked={value === true}
          disabled={readOnly}
          onChange={(e) => onChange(field.key, e.target.checked)}
          size="small"
          sx={{ p: "2px", color: "#7b92a9", "&.Mui-checked": { color: "#071f35" } }}
        />
      ) : (
        <TextField
          value={typeof value === "string" ? value : ""}
          disabled={readOnly}
          onChange={(e) => onChange(field.key, e.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            endAdornment: field.postfix ? (
              <InputAdornment position="end" sx={{ "& p": { fontSize: "13px", color: "#496682" } }}>
                {field.postfix}
              </InputAdornment>
            ) : undefined,
            sx: {
              fontSize: "13px",
              color: "#071f35",
              bgcolor: "white",
              "& input": { textAlign: isNumber ? "right" : "left", py: "5px", px: "8px" },
              "& fieldset": { borderColor: "#dbe8f5" },
              "&:hover fieldset": { borderColor: "#7b92a9" }
            }
          }}
          sx={{ width: "180px", flexShrink: 0 }}
        />
      )}
    </Stack>
  );
};
